import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { CopyButton } from "@/components/chat/Markdown";
import { Badge, Button, Card, Checkbox, ErrorText, Input, Label, Modal } from "@/components/ui";
import { api } from "@/lib/api";
import { useWorkers, type Worker } from "@/lib/agents";
import { useMe } from "@/lib/auth";
import { relTime } from "@/lib/tasks";

export default function WorkerSettings() {
  const qc = useQueryClient();
  const workers = useWorkers();
  const [editing, setEditing] = useState<Worker | "new" | null>(null);
  const [token, setToken] = useState<{ name: string; token: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["workers"] });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/workers/${id}`), onSuccess: refresh });
  const rotate = useMutation({
    mutationFn: (w: Worker) => api.post<{ token: string }>(`/api/workers/${w.id}/token`).then((r) => ({ name: w.name, token: r.token })),
    onSuccess: (t) => {
      setToken(t);
      refresh();
    },
  });

  return (
    <Card
      title="Workers"
      actions={
        <Button size="sm" onClick={() => setEditing("new")}>
          <Plus size={16} /> Add worker
        </Button>
      }
    >
      <p className="mb-3 text-sm text-zinc-500">
        A worker is a small program on another machine. It connects to this app and runs pi / oh-my-pi sessions and (if you allow it) shell commands
        there. Switch a worker on for a chat with the wrench button.
      </p>
      {workers.data?.length === 0 && <p className="text-sm text-zinc-500">No workers yet.</p>}
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {workers.data?.map((w) => (
          <li key={w.id} className="flex items-center gap-3 py-2.5">
            <span className={w.online ? "h-2.5 w-2.5 shrink-0 rounded-full bg-green-500" : "h-2.5 w-2.5 shrink-0 rounded-full bg-zinc-300 dark:bg-zinc-700"} title={w.online ? "Online" : "Offline"} />
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                {w.name}
                {(w.info.agents ?? []).map((a) => (
                  <Badge key={a} tone="indigo">
                    {a}
                  </Badge>
                ))}
                {w.info.exec && <Badge tone="amber">commands</Badge>}
                {!w.mine && <Badge>shared</Badge>}
                {w.groups.map((g) => (
                  <Badge key={g.id}>{g.name}</Badge>
                ))}
              </p>
              <p className="truncate text-xs text-zinc-500">
                {w.online ? `${w.info.host ?? ""} · ${w.info.os ?? ""}` : w.last_seen_at ? `last seen ${relTime(w.last_seen_at)}` : "never connected"}
              </p>
            </div>
            {w.mine && (
              <>
                <Button size="icon" variant="ghost" onClick={() => confirm("Make a new token? The worker must be restarted with it.") && rotate.mutate(w)} title="New token">
                  <KeyRound size={16} />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setEditing(w)} aria-label="Edit">
                  <Pencil size={16} />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => confirm(`Delete worker ${w.name}?`) && remove.mutate(w.id)} aria-label="Delete">
                  <Trash2 size={16} />
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>
      {editing && <WorkerForm worker={editing === "new" ? null : editing} onClose={() => setEditing(null)} onToken={setToken} />}
      {token && <TokenDialog {...token} onClose={() => setToken(null)} />}
    </Card>
  );
}

function WorkerForm({ worker, onClose, onToken }: { worker: Worker | null; onClose: () => void; onToken: (t: { name: string; token: string }) => void }) {
  const qc = useQueryClient();
  const me = useMe().data!;
  const [form, setForm] = useState({ name: worker?.name ?? "", exec_approval: worker?.exec_approval ?? true, group_ids: worker?.groups.map((g) => g.id) ?? [] });
  const save = useMutation({
    mutationFn: async () => {
      if (worker) return api.patch(`/api/workers/${worker.id}`, form);
      const r = await api.post<{ token: string }>("/api/workers", form);
      onToken({ name: form.name, token: r.token });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workers"] });
      onClose();
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }
  return (
    <Modal open onClose={onClose} title={worker ? "Edit worker" : "Add worker"}>
      <form className="space-y-4" onSubmit={submit}>
        <Label label="Name" hint="Used in tool names, e.g. homeserver__run_command.">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </Label>
        <Checkbox label="Ask me before running commands or agents from a chat" checked={form.exec_approval} onChange={(e) => setForm({ ...form, exec_approval: e.target.checked })} />
        <p className="-mt-2 text-xs text-zinc-500">Tasks that you set up to use this worker never ask.</p>
        {me.groups.length > 0 && (
          <div className="space-y-1.5">
            <span className="text-sm font-medium">Share with groups</span>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {me.groups.map((g) => (
                <Checkbox
                  key={g.id}
                  label={g.name}
                  checked={form.group_ids.includes(g.id)}
                  onChange={() => setForm({ ...form, group_ids: form.group_ids.includes(g.id) ? form.group_ids.filter((x) => x !== g.id) : [...form.group_ids, g.id] })}
                />
              ))}
            </div>
          </div>
        )}
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending}>{worker ? "Save" : "Create"}</Button>
        </div>
      </form>
    </Modal>
  );
}

function TokenDialog({ name, token, onClose }: { name: string; token: string; onClose: () => void }) {
  const server = location.origin;
  const cmd = `webthing-worker --server ${server} --token ${token}`;
  return (
    <Modal open onClose={onClose} title={`Worker token for ${name}`} wide>
      <div className="space-y-4 text-sm">
        <p>Copy the token now; it is not shown again.</p>
        <div className="flex items-center gap-2 rounded-lg bg-zinc-100 p-2 font-mono text-xs break-all dark:bg-zinc-800">
          <span className="min-w-0 flex-1">{token}</span>
          <CopyButton text={token} />
        </div>
        <p>
          On the machine, install the worker once with <code>uv tool install path/to/webthing/worker</code> (Python 3.11+), then start it. Agents
          found on its PATH (<code>pi</code>, <code>omp</code>) are offered automatically. Allowed directories and shell commands are set in a
          config file passed with <code>--config</code> (see <code>worker/worker.example.toml</code>).
        </p>
        <div className="flex items-start gap-2 rounded-lg bg-zinc-100 p-2 font-mono text-xs break-all dark:bg-zinc-800">
          <span className="min-w-0 flex-1">{cmd}</span>
          <CopyButton text={cmd} />
        </div>
        <div className="flex justify-end">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}
